import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Paintbrush, Armchair } from "lucide-react";

const transformations = [
  {
    title: "Living Room Modernization & Accent Styling",
    location: "Prestige Lakeside Habitat, Whitefield",
    beforeImage:
      "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1000&q=80",
    afterImage:
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1000&q=80",
    serviceLink: "/painting-services",
    serviceName: "Painting Services",
    serviceIcon: Paintbrush,
    highlights: "Royale Italian Stucco accent wall + dustless mechanized prep",
  },
  {
    title: "Master Suite Full Turnkey Furnishing",
    location: "Sobha Dream Acres, Panathur",
    beforeImage:
      "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1000&q=80",
    afterImage:
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1000&q=80",
    serviceLink: "/rental-furnitures",
    serviceName: "Rental Furnitures",
    serviceIcon: Armchair,
    highlights: "Solid oak king bed, memory mattress, modular vanity & smart desk",
  },
];

const GalleryTransformation = () => {
  const [sliderPositions, setSliderPositions] = useState({ 0: 50, 1: 50 });

  const handleSliderMove = (index, val) => {
    setSliderPositions((prev) => ({ ...prev, [index]: val }));
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-50 text-red-600 border border-red-100 text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5 text-red-600" />
            Visual Transformations
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 tracking-tight">
            Before & After Showcases
          </h2>
          <p className="mt-4 text-gray-600 text-base sm:text-lg">
            Drag the interactive slider to see how HousyHunt painting and designer furniture turnkey services revitalize empty spaces.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {transformations.map((item, idx) => {
            const Icon = item.serviceIcon;
            const pos = sliderPositions[idx] || 50;

            return (
              <div
                key={idx}
                className="bg-gradient-to-b from-gray-50 to-white rounded-3xl p-6 sm:p-7 border border-gray-200/90 shadow-lg flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-red-600">
                      {item.location}
                    </span>
                    <Link
                      to={item.serviceLink}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 hover:text-red-600 transition-colors"
                    >
                      <Icon className="w-3.5 h-3.5 text-red-500" />
                      <span>{item.serviceName}</span>
                    </Link>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mb-6">
                    {item.highlights}
                  </p>

                  {/* Interactive Slider Container */}
                  <div className="relative h-72 sm:h-80 rounded-2xl overflow-hidden shadow-inner select-none">
                    {/* After Image (Full background) */}
                    <img
                      src={item.afterImage}
                      alt="After Transformation"
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    {/* After Tag */}
                    <span className="absolute top-4 right-4 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10">
                      AFTER
                    </span>

                    {/* Before Image (Clipped) */}
                    <div
                      className="absolute inset-0 overflow-hidden"
                      style={{ width: `${pos}%` }}
                    >
                      <img
                        src={item.beforeImage}
                        alt="Before Transformation"
                        className="absolute inset-0 w-full h-full object-cover max-w-none"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <span className="absolute top-4 left-4 bg-zinc-800 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10">
                        BEFORE
                      </span>
                    </div>

                    {/* Divider Line */}
                    <div
                      className="absolute top-0 bottom-0 w-1 bg-white shadow-2xl cursor-ew-resize z-20 pointer-events-none"
                      style={{ left: `${pos}%` }}
                    >
                      <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow-xl flex items-center justify-center text-gray-800 text-xs font-bold border-2 border-red-500">
                        ↔
                      </div>
                    </div>

                    {/* Hidden Range Input covering container for drag */}
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={pos}
                      onChange={(e) => handleSliderMove(idx, e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-ew-resize w-full h-full z-30"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-400 font-medium">
                    Drag slider horizontally to compare
                  </span>
                  <Link
                    to={item.serviceLink}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 group"
                  >
                    <span>Book This Service</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default GalleryTransformation;
